import React, { useState } from "react";
import moment from "moment";
import { FiPlus, FiFilter, FiDownload, FiChevronDown, FiChevronUp, FiRotateCcw } from "react-icons/fi";

import {
  DateNavigation,
  EmptyState,
  FixtureCard,
  ViewToggle,
} from "../../components/Fixtures";

const FixtureListView = ({
  fixturesByDate = {},
  sortedDates = [],
  showConflicts,
  startDate,
  endDate,
  onDateChange,
  onShiftDates,
  isCalendarView,
  onToggleView,
  onOpenFilters,
  onOpenSwapTeams,
  onUndoSwap,
  undoableSwap,
  activeFiltersCount = 0,
  selectedTournament,
  onEditFixture,
  onDeleteFixture,
  onAddResult,
  onEditResult,
  onGenerateFixtures,
  onCreateFixture,
  categories = [],
  selectedCategory,
  onCategoryChange,
  isReadOnly = false,
  canManageFixtures = true,
  canAddResults = true,
  lockedPhaseIds = new Set(),
  isOwner = false,
}) => {
  const hasFixtures = sortedDates.length > 0;
  const [collapsedDates, setCollapsedDates] = useState({});

  const toggleDateCollapse = (date) => {
    setCollapsedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const exportToCSV = () => {
    const headers = [
      "Date",
      "Time",
      "Home Team",
      "Away Team",
      "Pitch",
      "Phase",
      "Group",
    ];

    const rows = [];

    Object.entries(fixturesByDate).forEach(([date, fixtures]) => {
      fixtures.forEach((fixture) => {
        const startMoment = fixture.matchTime ? moment(fixture.matchTime) : null;
        rows.push([
          startMoment ? startMoment.format("YYYY-MM-DD") : (date || ""),
          startMoment ? startMoment.format("HH:mm") : "",
          fixture.homeTeam?.title || fixture.homeTeam?.name || "TBD",
          fixture.awayTeam?.title || fixture.awayTeam?.name || "TBD",
          fixture.pitch || "",
          fixture.phaseName || "",
          fixture.groupName || "",
        ]);
      });
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "")}"`).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `fixtures-${selectedTournament?.name || "export"}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={!hasFixtures ? "flex-1 min-h-0 flex flex-col" : ""}>
      {/* Date Navigation & Actions Row - Always visible, matching Figma spacing */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:h-[44px] mb-6">
        <DateNavigation
          startDate={startDate ? new Date(startDate) : null}
          endDate={endDate ? new Date(endDate) : null}
          onDateChange={(start, end) => {
            if (start)
              onDateChange?.("start", moment(start).format("YYYY-MM-DD"));
            if (end)
              onDateChange?.("end", moment(end).format("YYYY-MM-DD"));
          }}
          onShiftDates={onShiftDates}
        />

        {/* Actions aligned right - exact Figma spacing gap-[8px] */}
        <div className="flex items-center gap-2 flex-wrap">
          {!isReadOnly && onOpenSwapTeams && (
            <button
              onClick={onOpenSwapTeams}
              className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
            >
              <span className="text-[#FEFCEB] font-medium text-[15px]">Team replacement</span>
            </button>
          )}

          {!isReadOnly && onUndoSwap && undoableSwap?.undoable && (
            <button
              onClick={onUndoSwap}
              title={
                undoableSwap.swap
                  ? `Undo: ${undoableSwap.swap.incomingTeamName} → ${undoableSwap.swap.outgoingTeamName}`
                  : "Undo last team replacement"
              }
              className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
            >
              <FiRotateCcw className="w-5 h-5 text-[#FEFCEB]" />
              <span className="text-[#FEFCEB] font-medium text-[15px]">Undo replacement</span>
            </button>
          )}

          <button
            onClick={exportToCSV}
            className="w-11 h-11 bg-app-card rounded-full flex items-center justify-center hover:opacity-50 transition-opacity"
            title="Download CSV"
          >
            <FiDownload className="w-5 h-5 text-[#FEFCEB]" />
          </button>

          <button
            onClick={onOpenFilters}
            className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
          >
            <FiFilter className="w-5 h-5 text-[#FEFCEB]" />
            <span className="text-[#FEFCEB] font-medium text-[15px]">
              Filters{activeFiltersCount > 0 && ` (${activeFiltersCount})`}
            </span>
          </button>

          

          <ViewToggle isCalendar={isCalendarView} onToggle={onToggleView} />
        </div>
      </div>

      {/* Content Area */}
      {!hasFixtures ? (
        <EmptyState
          onGenerateFixtures={onGenerateFixtures}
          onCreateFixture={onCreateFixture}
          hideButtons={isReadOnly}
        />
      ) : (
        <div className="space-y-4 mt-4">
          {sortedDates.map((date) => {
            const dateFixtures = fixturesByDate[date] || [];

            return (
              <div key={date} className="bg-blue-shade-8 p-3 rounded-2xl">
                <div
                  className="flex items-center gap-2 cursor-pointer group select-none"
                  onClick={() => toggleDateCollapse(date)}
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
                    {collapsedDates[date] ? (
                      <FiChevronDown className="w-5 h-5 text-[#FEFCEB]" />
                    ) : (
                      <FiChevronUp className="w-5 h-5 text-[#FEFCEB]" />
                    )}
                  </div>
                  <h3 className="text-[#FEFCEB] text-[16px] font-medium m-0">
                    {moment(date).format("ddd, D MMM")}
                  </h3>
                </div>

                {!collapsedDates[date] && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                    {dateFixtures.map((fixture) => (
                      <FixtureCard
                        key={fixture._id || fixture.id}
                        fixture={fixture}
                        result={fixture.eventId || null}
                        onEdit={onEditFixture}
                        onDelete={onDeleteFixture}
                        onAddResult={onAddResult}
                        onEditResult={onEditResult}
                        showConflicts={showConflicts}
                        hideDate
                        isReadOnly={isReadOnly}
                        canManageFixtures={canManageFixtures}
                        canAddResults={canAddResults}
                        isLocked={lockedPhaseIds.has(fixture.phaseId)}
                        isOwner={isOwner}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FixtureListView;
